import {
  FC,
  useEffect,
  useState,
  useRef,
  useCallback,
  KeyboardEvent,
  useLayoutEffect,
  useMemo,
  RefObject,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import { ExpandingTextArea } from '../ExpandingTextArea';
import { toastVariant, useToast } from '__components/ToastProvider';
import { MessageHistory } from './MessageHistory';
import { QuickResponses, QuickResponseData } from '../QuickResponses';
import { Controls } from './Controls';
import { KZN_FRAME_EVENTS } from '__pages/BusinessBuilderPublic/events';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import { deferExecution } from 'utility/defer';
import IconButtonAbsoluteMenu from '__components/Tables/Big/IconButtonAbsoluteMenu';
import Icon from '__components/Kizen/Icon';
import { colorsButton } from '__app/colors';
import { ADD_MORE_DETAILS_OPTION } from '../ChatBot';
import { isNonComposingEnterKeyboardEvent } from 'utility/keyboard';
import { useKizenAiFrameMessage } from 'pages/AIChatEmbed/useKizenAiFrameMessage';
import KizenAILogo from 'pages/Marketplace/assets/kizen-ai.svg?react';
import type { PreviewItem } from 'ts-components/ChatBotV2/types';

export type MessageType = {
  id: string;
  isUserMessage: boolean;
  isResponseLoading: boolean;
  isResponseRefining?: boolean;
  messageContent: string;
  bubble?: boolean;
  showAvatar?: boolean;
  floatAbove?: boolean;
  header?: string;
  eventName?: string;
  streaming?: boolean;
  isDoneStreaming?: boolean;
  disableAutoScroll?: boolean;
  showFeedbackIcons?: boolean;
  role?: 'user' | 'assistant' | 'system';
  awaitingFinal?: boolean;
  buildCompleted?: boolean;
  answer_mode?: string;
  last_flow?: any;
  isErrorMessage?: boolean;
  statusDetails?: string;
  previewData?: PreviewItem[];
};

type ChatProps = {
  header?: string;
  showLogo?: boolean;
  headerLogoUrl?: string | null;
  messages: MessageType[];
  quickResponseData: QuickResponseData;
  quickResponsesHeader: string;
  renderMarkdown?: boolean;
  selectedQuickResponses: { id: string; value: string }[];
  onQuickResponseSelect: (value: string, type: string, url?: string) => void;
  alert: string;
  errorMessage?: string;
  onSendMessage: (message: string) => void;
  onCreateNewChat: (context?: Record<string, unknown>) => void;
  onAlert: () => void;
  onCompleteSession: () => void;
  placeholder: string;
  entityName?: string;
  isLoadingNewSession: boolean;
  isMessageResponseLoading: boolean;
  isCompleteSessionLoading: boolean;
  showCompleteSessionButton?: boolean;
  showSkipQuestionsButton?: boolean;
  showAskMoreQuestionsButton?: boolean;
  disableCompeteSessionButton?: boolean;
  disableSkipQuestionsButton?: boolean;
  disableSendMessageButton?: boolean;
  showOneShotGenerationButton?: boolean;
  disableOneShotGenerationButton?: boolean;
  showChatButton?: boolean;
  disableChatButton?: boolean;
  oneShotOnly?: boolean;
  hideRefresh?: boolean;
  hideLoadingSpinner?: boolean;
  disableMessageAnimation?: boolean;
  trackEvent?: (event: string, payload?: any) => void;
  showFixedHeight?: boolean;
  hideControllerButtons?: boolean;
  headerMenuOptions?: Array<{ value: string; label: string }>;
  handleHeaderMenuOptionSelect?: ({ value }: { value: string }) => void;
  actionsToScrollUp?: string[];
  actionsToScrollDown?: string[];
  onCopyMessage?: (messageContent: string) => void;
  onLikeMessage?: () => void;
  onDislikeMessage?: () => void;
  onRefreshMessage?: () => void;
  onChatButtonClick?: (message: string) => void;
  messageHistoryRef?: RefObject<HTMLDivElement>;
  shouldFocusInput?: boolean;
  disableScrollToBottom?: boolean;
  hideScrollbar?: boolean;
  disableTextAreaMessage?: string;
  onPreviewSelect?: (preview: PreviewItem) => void;
};

export const chatDefaultPadding = 'padding: 0 200px 0 200px;';

export const mobileChatDefaultPadding = 'padding: 0 10px 0 10px;';

export const Chat: FC<ChatProps> = ({
  header,
  showLogo = false,
  headerLogoUrl,
  messages = [],
  quickResponseData,
  quickResponsesHeader,
  selectedQuickResponses,
  onQuickResponseSelect,
  alert = '',
  onSendMessage,
  onCreateNewChat,
  isLoadingNewSession,
  isMessageResponseLoading,
  isCompleteSessionLoading,
  showCompleteSessionButton = false,
  showSkipQuestionsButton = false,
  showAskMoreQuestionsButton = false,
  disableCompeteSessionButton = false,
  disableSkipQuestionsButton = false,
  disableSendMessageButton = false,
  showOneShotGenerationButton = false,
  disableOneShotGenerationButton = false,
  showChatButton = false,
  disableChatButton = false,
  oneShotOnly = false,
  hideRefresh = false,
  hideLoadingSpinner = false,
  disableMessageAnimation = false,
  renderMarkdown,
  onCompleteSession,
  onAlert,
  placeholder,
  entityName,
  trackEvent,
  showFixedHeight,
  hideControllerButtons,
  headerMenuOptions,
  actionsToScrollUp,
  actionsToScrollDown,
  handleHeaderMenuOptionSelect,
  onCopyMessage,
  onLikeMessage,
  onDislikeMessage,
  onRefreshMessage,
  onChatButtonClick,
  messageHistoryRef,
  shouldFocusInput,
  disableScrollToBottom = false,
  hideScrollbar = false,
  disableTextAreaMessage,
  onPreviewSelect,
}) => {
  const { t } = useTranslation();
  const defaultMessageHistoryRef = useRef<HTMLDivElement>(null);
  const messageHistoryWrapperRef =
    messageHistoryRef || defaultMessageHistoryRef;
  const displayAlertToastRef = useRef<boolean>(true);
  const [focusTextArea, setFocusTextArea] = useState(false);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const lastAssistantMessageRef = useRef<HTMLDivElement>(null);
  const scrollBottomRef = useRef<HTMLDivElement>(null);
  const quickResponsesRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const hasUserSentMessageRef = useRef<boolean>(false);
  const [showToast] = useToast();

  const [newMessage, setNewMessage] = useState<string>('');
  const [shouldTruncateQuickResponses, setShouldTruncateQuickResponses] =
    useState(false);

  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
  const [additonalScrollHeight, setAdditonalScrollHeight] = useState(0);
  const [isPinnedToTop, setIsPinnedToTop] = useState(false);
  const pinBaselineRef = useRef<{ active: boolean; scrollHeight: number }>({
    active: false,
    scrollHeight: 0,
  });

  const PIN_TOP_OFFSET = 25;

  const calculateScrollHeight = useCallback(() => {
    const messagesContainer = messageHistoryWrapperRef.current;
    const lastUserMessageContainer = lastUserMessageRef.current;
    if (!messagesContainer || !lastUserMessageContainer) return;

    setAdditonalScrollHeight(
      Math.max(
        0,
        messagesContainer.clientHeight -
          (lastUserMessageContainer.clientHeight + 75)
      )
    );
  }, [messageHistoryWrapperRef, lastUserMessageRef]);

  // calculate new scroll height
  useEffect(() => {
    const messagesWrapperContainer = messageHistoryWrapperRef.current;
    if (!messagesWrapperContainer || !hasUserSentMessageRef.current) return;
    const ro = new ResizeObserver(() => calculateScrollHeight());
    ro.observe(messagesWrapperContainer);
    return () => ro.disconnect();
  }, [calculateScrollHeight, messageHistoryWrapperRef]);

  // Recalculate the scroll spacer height before paint whenever messages change
  useLayoutEffect(() => {
    if (!hasUserSentMessageRef.current) return;
    calculateScrollHeight();
  }, [messages, calculateScrollHeight]);

  const scrollUp = useCallback(() => {
    const container = messageHistoryWrapperRef.current;
    const target = lastUserMessageRef.current;
    if (!container || !target) return;

    // final scrollTop such that last user msg sits at PIN_TOP_OFFSET
    const targetOffset =
      target.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      PIN_TOP_OFFSET;

    setAdditonalScrollHeight(
      Math.max(0, container.clientHeight - (target.clientHeight + 75))
    );
    container.scrollTo({ top: targetOffset, behavior: 'smooth' });
    setIsPinnedToTop(true);
    setIsAutoScrollEnabled(false);

    requestAnimationFrame(() => {
      pinBaselineRef.current = {
        active: true,
        scrollHeight: container.scrollHeight,
      };
      container.scrollTo({ top: targetOffset, behavior: 'smooth' });
    });
  }, [messageHistoryWrapperRef]);

  const scrollDown = useCallback(() => {
    const container = messageHistoryWrapperRef.current;
    const target = lastAssistantMessageRef.current;
    const quickResponses = quickResponsesRef.current;
    if (!container || !target) return;

    const targetRect = target.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const quickResponsesHeight = quickResponses?.clientHeight || 0;

    // Check if element is already in view
    const isTargetVisible =
      targetRect.top >= containerRect.top &&
      targetRect.bottom <= containerRect.bottom - quickResponsesHeight;

    if (isTargetVisible) {
      setIsPinnedToTop(false);
      setIsAutoScrollEnabled(true);
      pinBaselineRef.current = { active: false, scrollHeight: 0 };
      return;
    }

    const targetOffset =
      targetRect.top -
      containerRect.top +
      container.scrollTop -
      (container.clientHeight - targetRect.height - quickResponsesHeight - 10);

    container.scrollTo({ top: targetOffset, behavior: 'smooth' });
    setIsPinnedToTop(false);
    setIsAutoScrollEnabled(true);
    pinBaselineRef.current = { active: false, scrollHeight: 0 };
  }, [messageHistoryWrapperRef]);

  // Auto-scroll to bottom
  useEffect(() => {
    const container = messageHistoryWrapperRef.current;
    const bottomEl = scrollBottomRef.current;
    if (!container || !bottomEl) return;

    if (isPinnedToTop || !isAutoScrollEnabled) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting && !disableScrollToBottom) {
          // Scroll the container directly instead of using scrollIntoView to prevent page scrolling
          container.scrollTo({ top: container.scrollHeight, behavior: 'auto' });
        }
      },
      { root: container, threshold: 0.1 }
    );

    observer.observe(bottomEl);
    return () => observer.disconnect();
  }, [
    messages,
    isAutoScrollEnabled,
    isPinnedToTop,
    messageHistoryWrapperRef,
    disableScrollToBottom,
  ]);

  const lastMessagePreviewId = messages[messages.length - 1]?.previewData
    ?.length
    ? messages[messages.length - 1].id
    : null;
  const previewSpacerRef = useRef<HTMLDivElement>(null);
  const lastScrollTopRef = useRef(0);

  // Size the preview spacer and restore scroll position before paint, then
  // smooth scroll the preview cards into view
  useLayoutEffect(() => {
    if (!lastMessagePreviewId) return;
    const container = messageHistoryWrapperRef.current;
    const target = scrollBottomRef.current;
    if (!container || !target) return;

    // number of pixels of scrollable height that are missing when the preview cards mount
    const deficit = Math.ceil(
      lastScrollTopRef.current + container.clientHeight - container.scrollHeight
    );
    if (deficit > 0 && previewSpacerRef.current) {
      previewSpacerRef.current.style.height = `${deficit}px`;
      container.scrollTop = lastScrollTopRef.current;
    }

    const targetRect = target.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    if (targetRect.bottom <= containerRect.bottom) return;

    const targetOffset =
      targetRect.top -
      containerRect.top +
      container.scrollTop -
      (container.clientHeight - 10);

    container.scrollTo({ top: targetOffset, behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessagePreviewId]);

  const handleScroll = useCallback(() => {
    const container = messageHistoryWrapperRef.current;
    if (!container) return;

    lastScrollTopRef.current = container.scrollTop;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 25;

    if (isPinnedToTop) {
      const overflowedPastBaseline =
        pinBaselineRef.current.active &&
        container.scrollHeight > pinBaselineRef.current.scrollHeight + 1;

      if (overflowedPastBaseline && isAtBottom) {
        setIsPinnedToTop(false);
        pinBaselineRef.current = { active: false, scrollHeight: 0 };
        setIsAutoScrollEnabled(true);
      }
    }

    setIsAutoScrollEnabled(isAtBottom);
  }, [isPinnedToTop, messageHistoryWrapperRef]);

  const initiallyPinnedToTop = useRef<boolean>(false);

  // Attach scroll listener to detect position
  useEffect(() => {
    const wrapper = messageHistoryWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener('scroll', handleScroll);
      return () => wrapper.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll, messageHistoryWrapperRef]);

  // handle alerts
  useEffect(() => {
    if (displayAlertToastRef.current && alert) {
      trackEvent?.(KZN_FRAME_EVENTS.SESSION_FLAGGED);
      displayAlertToastRef.current = false;
      showToast({
        message: `${t('Your request has been flagged for not following CoBuilder guidelines')}`,
        variant: toastVariant.FAILURE,
      });
      onAlert();
    }
  }, [alert, showToast, onAlert, t, trackEvent]);

  // focus text area
  useEffect(() => {
    if (focusTextArea) {
      document.getElementById('message-input')?.focus({ preventScroll: true });
      setFocusTextArea(false);
    }
  }, [focusTextArea]);

  // Pin last user message on initial load with existing messages
  useEffect(() => {
    if (
      messages.length > 1 &&
      !hasUserSentMessageRef.current &&
      !initiallyPinnedToTop.current
    ) {
      initiallyPinnedToTop.current = true;
      setIsPinnedToTop(true);
      setIsAutoScrollEnabled(false);
      deferExecution(() => scrollUp());
    }
  }, [messages.length, scrollUp]);

  const addMoreDetailsSelected = useMemo(
    () =>
      Boolean(
        selectedQuickResponses.find(
          (pill) => pill.value === ADD_MORE_DETAILS_OPTION
        )
      ),
    [selectedQuickResponses]
  );

  const shouldTruncateRef = useRef(false);
  const untruncatedHeightRef = useRef<number | null>(null);

  // Determine if quick responses should be truncated
  useLayoutEffect(() => {
    const chatContainer = chatContainerRef.current;
    const quickResponses = quickResponsesRef.current;
    if (!chatContainer || !quickResponses) return;

    untruncatedHeightRef.current = null;
    shouldTruncateRef.current = false;
    setShouldTruncateQuickResponses(false);

    const checkHeight = () => {
      const containerHeight = chatContainer.clientHeight;
      const quickResponsesHeight = quickResponses.clientHeight;
      const threshold = containerHeight * 0.4;

      if (!shouldTruncateRef.current) {
        untruncatedHeightRef.current = quickResponsesHeight;
      }
      const heightToCompare =
        untruncatedHeightRef.current ?? quickResponsesHeight;
      const shouldTruncate = heightToCompare > threshold;

      if (shouldTruncate !== shouldTruncateRef.current) {
        shouldTruncateRef.current = shouldTruncate;
        setShouldTruncateQuickResponses(shouldTruncate);
      }
    };

    requestAnimationFrame(checkHeight);
    const resizeObserver = new ResizeObserver(checkHeight);
    resizeObserver.observe(chatContainer);

    return () => resizeObserver.disconnect();
  }, [quickResponseData.options]);

  // Auto-focus input when shouldFocusInput is true
  useEffect(() => {
    if (shouldFocusInput || addMoreDetailsSelected) {
      setFocusTextArea(true);
    }
  }, [shouldFocusInput, addMoreDetailsSelected]);

  const handleSendMessage = useCallback(
    (value: string) => {
      if (disableSendMessageButton) return;
      hasUserSentMessageRef.current = true;
      trackEvent?.(KZN_FRAME_EVENTS.CHAT_USER);
      setIsAutoScrollEnabled(false);
      setNewMessage('');
      onSendMessage(value);
      deferExecution(() => scrollUp());
    },
    [disableSendMessageButton, onSendMessage, trackEvent, scrollUp]
  );

  const [pendingExternalMessage, setPendingExternalMessage] =
    useState<string>('');

  useKizenAiFrameMessage(
    (data) => {
      if (data.action === 'sendUserMessage') {
        if (data.createNewSession) {
          onCreateNewChat({
            graphName: data.graphName,
            graphTag: data.graphTag,
          });
          setPendingExternalMessage((data.message as string) || '');
        } else {
          setPendingExternalMessage('');
          handleSendMessage((data.message as string) || '');
        }
      }
    },
    { iframeOnly: true }
  );

  // Send a queued external (frame) message once the session is ready
  useEffect(() => {
    if (
      pendingExternalMessage &&
      !isLoadingNewSession &&
      !isMessageResponseLoading
    ) {
      handleSendMessage(pendingExternalMessage);
      setPendingExternalMessage('');
    }
  }, [
    pendingExternalMessage,
    isLoadingNewSession,
    isMessageResponseLoading,
    handleSendMessage,
  ]);

  const handleKeydown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (isNonComposingEnterKeyboardEvent(e) && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage(newMessage.trim());
      }
    },
    [newMessage, handleSendMessage]
  );

  const disableTextArea = useMemo(
    () =>
      Boolean(alert) ||
      Boolean(disableTextAreaMessage) ||
      (quickResponseData.selectType === 'multi_select' &&
        selectedQuickResponses.length > 0 &&
        !addMoreDetailsSelected),
    [
      disableTextAreaMessage,
      quickResponseData.selectType,
      addMoreDetailsSelected,
      selectedQuickResponses.length,
      alert,
    ]
  );

  const handleQuickResponseSelect = useCallback(
    (value: string, type: string, url?: string) => {
      onQuickResponseSelect(value, type, url);
      displayAlertToastRef.current = true;
      if (
        type === 'single_select' ||
        (type === 'action' && actionsToScrollUp?.includes(value))
      ) {
        hasUserSentMessageRef.current = true;
        setIsAutoScrollEnabled(false);
        deferExecution(() => scrollUp());
      } else if (type === 'action' && actionsToScrollDown?.includes(value)) {
        deferExecution(() => scrollDown());
      }
    },
    [
      onQuickResponseSelect,
      scrollUp,
      scrollDown,
      actionsToScrollUp,
      actionsToScrollDown,
    ]
  );

  return (
    <div
      ref={chatContainerRef}
      className="flex flex-col w-full h-full bg-background-white"
    >
      {header || showLogo ? (
        <div
          className="flex w-full p-[10px] gap-[10px] h-[50px] items-center"
          style={{
            background:
              'linear-gradient(to right, var(--color-ai-gradient-start), var(--color-ai-gradient-end))',
          }}
        >
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center gap-[10px]">
              {showLogo ? (
                <div className="flex w-[30px] h-[30px]">
                  {headerLogoUrl ? (
                    <img
                      src={headerLogoUrl}
                      alt="Kizen AI"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <KizenAILogo className="w-full h-full" />
                  )}
                </div>
              ) : null}
              {header ? (
                <Typography size="2xl" weight="semibold" color="ai-system-text">
                  {t('{{header}}', { header })}
                </Typography>
              ) : null}
            </div>
            {headerMenuOptions ? (
              <IconButtonAbsoluteMenu
                position="left"
                sizing="dense"
                color={colorsButton.iconGray}
                onChange={handleHeaderMenuOptionSelect}
                options={headerMenuOptions}
                title={t('Options')}
                restrictCloseHandler
                data-qa="kizen-ai-header-menu"
              >
                <Icon icon="three-dot" />
              </IconButtonAbsoluteMenu>
            ) : null}
          </div>
        </div>
      ) : null}
      <div className="flex flex-col overflow-hidden relative flex-1 pb-[5px]">
        <div className="absolute top-[-15x] left-0 w-full pointer-events-none">
          <Gradient
            fadeStart={0}
            fadeLength={20}
            color="var(--color-background-white)"
            position="top"
            fadeIn
            zIndex={2}
            borderRadius="0"
          />
        </div>
        <div className="absolute bottom-[5px] left-0 w-full pointer-events-none">
          <Gradient
            fadeStart={0}
            fadeLength={20}
            color="var(--color-background-white)"
            position="bottom"
            fadeIn
            zIndex={2}
          />
        </div>
        <MessageHistory
          messageHistoryWrapperRef={messageHistoryWrapperRef}
          messages={messages}
          isLoadingNewSession={isLoadingNewSession}
          isMessageResponseLoading={isMessageResponseLoading}
          lastUserMessageRef={lastUserMessageRef}
          lastAssistantMessageRef={lastAssistantMessageRef}
          scrollBottomRef={scrollBottomRef}
          scrollToHeight={additonalScrollHeight}
          previewSpacerRef={previewSpacerRef}
          showFixedHeight={showFixedHeight}
          hasUserSentAMessage={
            hasUserSentMessageRef.current || messages.length === 1
          }
          renderMarkdown={renderMarkdown}
          hideRefresh={hideRefresh}
          hideLoadingSpinner={hideLoadingSpinner}
          disableMessageAnimation={disableMessageAnimation}
          onCopyMessage={onCopyMessage}
          onLikeMessage={onLikeMessage}
          onDislikeMessage={onDislikeMessage}
          onRefreshMessage={onRefreshMessage}
          onPreviewSelect={onPreviewSelect}
          hideScrollbar={hideScrollbar}
        />
      </div>
      <div
        ref={quickResponsesRef}
        className="flex flex-col flex-shrink-0 bg-white animate-fadeIn"
      >
        <QuickResponses
          header={quickResponsesHeader}
          isMessageResponseLoading={isMessageResponseLoading}
          data={quickResponseData}
          onSelect={handleQuickResponseSelect}
          selectedResponses={selectedQuickResponses}
          handleSubmit={() => handleSendMessage(newMessage.trim())}
          onKeyDown={handleKeydown}
          maxHeight={shouldTruncateQuickResponses ? 2 : undefined}
          showSendButton={
            selectedQuickResponses.length > 0 && !addMoreDetailsSelected
          }
          noTruncateOption={ADD_MORE_DETAILS_OPTION}
        />
        <div className="mt-[10px] w-full h-full">
          <ExpandingTextArea
            id="message-input"
            placeholder={placeholder}
            disabledMessage={
              alert
                ? t('Restart chat to send a new message.')
                : disableTextAreaMessage
                  ? disableTextAreaMessage
                  : disableTextArea
                    ? t(
                        'Deselect all quick responses to type your own custom response.'
                      )
                    : undefined
            }
            value={newMessage}
            onChange={setNewMessage}
            disabled={disableTextArea}
            disableSendButton={
              newMessage.trim() === '' ||
              isLoadingNewSession ||
              isMessageResponseLoading ||
              isCompleteSessionLoading ||
              disableSendMessageButton
            }
            onKeyDown={handleKeydown}
            handleSubmit={() => handleSendMessage(newMessage.trim())}
            showOneShotGenerationButton={showOneShotGenerationButton}
            disableOneShotGenerationButton={disableOneShotGenerationButton}
            disableChatButton={disableChatButton}
            showChatButton={showChatButton}
            oneShotOnly={oneShotOnly}
            showSubmitButton={
              selectedQuickResponses.length === 0 ||
              addMoreDetailsSelected ||
              quickResponseData.selectType !== 'multi_select'
            }
            onChatButtonClick={() => {
              if (onChatButtonClick) {
                onChatButtonClick(newMessage.trim());
                setNewMessage('');
              }
            }}
          />
        </div>
        {!hideControllerButtons ? (
          <Controls
            trackEvent={trackEvent}
            entityName={entityName}
            minHeight={36}
            setNewMessage={setNewMessage}
            onSendMessage={handleSendMessage}
            onCreateNewChat={() => {
              displayAlertToastRef.current = true;
              onCreateNewChat();
            }}
            setDisplayAlertToast={() => (displayAlertToastRef.current = true)}
            newMessage={newMessage}
            onCompleteSession={() => {
              onCompleteSession();
            }}
            showBuildApplicationButton={showCompleteSessionButton}
            showSkipQuestionsButton={showSkipQuestionsButton}
            showAskMoreQuestionsButton={showAskMoreQuestionsButton}
            showCreateNewChatButton={messages.length > 1}
            disableBuildApplicationButton={
              isCompleteSessionLoading || disableCompeteSessionButton
            }
            disableSkipQuestionsButton={
              Boolean(alert) ||
              isLoadingNewSession ||
              isMessageResponseLoading ||
              isCompleteSessionLoading ||
              disableSkipQuestionsButton
            }
            disableCreateNewChatButton={isLoadingNewSession}
            disableAskMoreQuestionsButton={
              Boolean(alert) ||
              isLoadingNewSession ||
              isMessageResponseLoading ||
              isCompleteSessionLoading ||
              disableSendMessageButton
            }
          />
        ) : null}
      </div>
    </div>
  );
};
